import { useEffect, useState } from "react";
import { PageContainer } from "../components/layout/page";
import { useParams } from "react-router-dom";
import { HeroImage, SizedImage, TalentImage } from "../components/ui/file-contents";
import { clearSteamLink, getColorByPlace, getHeroFriendlyName, getRatingChange, getShortString, getValueOrDefault, toDateWithTime } from "../../common/utils";
import { Table } from "../components/ui/tables";
import { SubscribeGlobalState } from "../../common/hooks/global-state";
import { EditProfileButton, LogoutButton, PaginationButtons } from "../components/functional/buttons";
import { SocialLink } from "../components/functional/hyper-links";
import { Places } from "../components/ui/other";
import { AuthorizedUser } from "../../api/repositories/backend/models/auth";
import { MatchesRepository } from "../../api/repositories/stats/matches";
import { MatchHistory } from "../../api/repositories/stats/models/matches";
import { PlayerProfile, ShortSteamPlayer } from "../../api/repositories/stats/models/players";
import { PlayersRepository } from "../../api/repositories/stats/players";
import { NotFoundBlock } from "../components/layout/errors";
import { localize } from "../../common/services/localization";
import { EditMediaPopup } from "../components/popups/edit-media-popup";
import { Socials, SocialsWithState } from "../../api/repositories/shared/models/players";
import { EditProfileIcon, LoadingIcon, SocialIcon } from "../components/ui/icons";

export const Player: React.FC = () => {
    const { id } = useParams();

    const authorizedUser = SubscribeGlobalState('authorizedUser') as AuthorizedUser | undefined;
    const heroes = SubscribeGlobalState('heroes');

    const [responsePlayerProfile, setResponsePlayerProfile] = useState<ApiDataResponse<PlayerProfile> | undefined>();
    const [responseSteamProfile, setResponseSteamProfile] = useState<ApiDataResponse<ShortSteamPlayer> | undefined>();

    useEffect(() => {
        const repository = new PlayersRepository();

        if (id) {
            const parsedId = parseInt(id);

            if (!isNaN(parsedId)) {
                repository.get(parsedId).then(v => setResponsePlayerProfile(v));
                repository.getSteamProfile(parsedId).then(v => setResponseSteamProfile(v));
            }
        }
    }, [id]);

    const playerNotFoundBlock = <NotFoundBlock
        localText="player_not_found_title"
        onClickBtn={() => window.location.href = '/'}
        btnLocalText="player_not_found_btn"
        withImg={false}
    />

    return <PageContainer
        containerClassName='max-w-[1561px] mt-[90px]'
        headerImgPath='/v2/images/bg/towers.png'
        child={
            <div>
                {
                    !id || isNaN(parseInt(id))
                        ? playerNotFoundBlock
                        : <div className={`w-full flex flex-col xl:min-h-screen 2xl:flex-row ${responsePlayerProfile?.hasErrors ? '' : 'justify-center'}`}>
                            <div className="flex flex-col 2xl:flex-row justify-between gap-[24px]">
                                {
                                    responsePlayerProfile
                                        ? responsePlayerProfile?.hasErrors
                                            ? playerNotFoundBlock
                                            : <>
                                                <PlayerProfileBlock
                                                    authorizedUser={authorizedUser}
                                                    player={responsePlayerProfile.data!}
                                                    steamProfile={responseSteamProfile?.data}
                                                />
                                                <GamesHistoryBlock
                                                    playerId={parseInt(id)}
                                                    heroes={heroes ?? []}
                                                />
                                            </>
                                        : <LoadingIcon />
                                }
                            </div>
                        </div>
                }
            </div>
        }
    />
}

const GamesHistoryBlock: React.FC<{
    playerId: number
    heroes: Hero[]
}> = ({ playerId, heroes }) => {
    const repository = new MatchesRepository();
    const valuesOnPageCount = 20;

    const [response, setResponse] = useState<ApiPaginationResponse<MatchHistory> | undefined>();
    const [page, setGamesPage] = useState(1);

    const handlePageChange = (page: number) => {
        setGamesPage(page);
        getPageValues(page);
    };

    useEffect(() => {
        getPageValues(page)
    }, [])

    function getPageValues(page: number) {
        repository.getHistory(playerId, page, valuesOnPageCount)
            .then(v => {
                if (v.hasErrors)
                    return console.log(v.errors);

                setResponse(v);
            });
    }

    const tableHeaderDefaultClassName = "text-[10px] lg:text-[16px] font-normal text-gray font-['Roboto'] text-center uppercase ";
    const tableColClassName = "text-[12px] lg:text-[16px]";

    return <div className="flex flex-col gap-[32px] max-w-[1032px] xl:min-w-[1032px]  2xl:w-[1032px] bg-dark shadow-dark-l py-[32px] lg:p-[32px]" >
        <h4 className="uppercase px-[32px] lg:px-[0px]">
            {localize('player_last_games_title')}
        </h4>
        {
            response
                ? <div>
                    <Table
                        cols={[
                            { headLocalName: "player_last_games_table_hero", className: `ps-[18px] w-[50px] lg:w-[200px] text-start ${tableHeaderDefaultClassName}` },
                            { headLocalName: "player_last_games_table_talent", className: `w-[50px] lg:w-[99px] ${tableHeaderDefaultClassName}` },
                            { headLocalName: "status", className: `w-[50px] lg:w-[108px] ${tableHeaderDefaultClassName}` },
                            { headLocalName: "player_last_games_table_place", className: `w-[50px] lg:w-[90px] ${tableHeaderDefaultClassName}` },
                            { headLocalName: "player_last_games_table_rating", className: `w-[50px] lg:w-[108px] ${tableHeaderDefaultClassName}` },
                            { headLocalName: "player_last_games_table_date", className: `w-[50px] lg:w-[159px] ${tableHeaderDefaultClassName}` },
                        ]}
                        rows={response?.data.values.map((game, i) => ({
                            href: `/matches/${game.matchId}`,
                            className: `text-16 font-['Roboto'] font-normal text-center ${i == response?.data.values.length - 1 ? '' : 'border-b-dark'}`,
                            cols: [
                                {
                                    className: "lg:ps-[18px] w-[50px] lg:w-[254px] w-full",
                                    value: game.heroName
                                        ? <div className="flex flex-row items-center gap-[8px]">
                                            <HeroImage className="shadow-dark-sm h-[35px] lg:h-[47px]" heroName={game.heroName} size="80x47" />
                                            <div className="hidden lg:block">
                                                {getHeroFriendlyName(game.heroName, heroes)}
                                            </div>
                                        </div>
                                        : <span className="flex mx-auto justify-center items-center h-[35px] lg:h-[47px]">
                                            {getValueOrDefault()}
                                        </span>,
                                },
                                {
                                    value: game.talent
                                        ? <div className="flex flex-row items-center justify-center">
                                            <TalentImage className="h-[35px] lg:h-[47px] shadow-dark-sm" name={game.talent ?? ''} size="47x47" />
                                        </div>
                                        : getValueOrDefault(),
                                    className: 'w-[50px] lg:w-[99px] '
                                },
                                { value: localize(`match_status_${game.isFinished}`), className: `w-[50px] lg:w-[109px] ${tableColClassName}` },
                                { value: getValueOrDefault(game.place), className: `w-[50px] lg:w-[90px] ${getColorByPlace(game.place)} ${tableColClassName}` },
                                { value: getValueOrDefault(getRatingChange(game.ratingChange, game.isDoubleRating)), className: `w-[50px] lg:w-[109px] ${getColorByPlace(game.place)} ${tableColClassName}` },
                                { value: getValueOrDefault(toDateWithTime(game.createdAtUtc)), className: `w-[50px] lg:w-[129px] ${tableColClassName}` },
                            ],
                        }))}
                    />
                    {
                        response.data.totalCount <= valuesOnPageCount
                            ? null
                            : <PaginationButtons
                                className="mt-[32px]"
                                response={response}
                                currentPage={page}
                                onClick={handlePageChange}
                            />
                    }
                </div>
                : <></>
        }
    </div>
}

const PlayerProfileBlock: React.FC<{
    player: PlayerProfile
    steamProfile?: ShortSteamPlayer
    authorizedUser?: AuthorizedUser
}> = ({ player, steamProfile, authorizedUser }) => {
    const [isEditMediaOpen, setIsEditMediaOpen] = useState(false);
    const [currentSocials, setCurrentSocials] = useState(player.social);

    const handleSaveSocials = async (newSocials: Socials) => {
        setCurrentSocials(newSocials);
        setIsEditMediaOpen(false);
    };

    const isAuthorizedUser = authorizedUser && authorizedUser.playerId === player.playerId;
    const canChangeSocials = isAuthorizedUser && !!!authorizedUser.isBannedOnSocials;

    const socialLinkSize = 'w-[28px] h-[28px] lg:w-[28px] lg:h-[28px]'
    const statiscTitleClass = "uppercase font-['Roboto'] text-[12px] sm:text-[16px] text-gray mb-[8px]"
    const statiscValueClass = "text-[18px] md:text-[28px] font-semibold"


    return <div className="flex flex-col gap-[24px]" >
        <div className="w-[370px] sm:w-[505px] h-[224px] bg-dark shadow-dark-l p-[32px]">
            <div className="flex justify-between">
                <h4 className="uppercase justify-start">
                    {localize("player_title")}
                </h4>
                {isAuthorizedUser && <LogoutButton />}
            </div>
            <div className="flex justify-between items-end">
                <div className="flex flex-row mt-[12px] sm:mt-[32px] gap-[16px]">
                    <SizedImage
                        className="hidden sm:block w-[100px] h-[100px] shadow-dark-sm"
                        src={steamProfile ? steamProfile.avatarfull : ''}
                    />
                    <div className="flex flex-col justify-between gap-[40px] sm:gap-[0]">
                        <div className="text-[16px] lg:text-[22px] font-['Roboto'] ">
                            <div className=""> {getShortString(steamProfile?.personaname ?? '', 21)}</div>
                            <div className="flex flex-row gap-[12px]">
                                {currentSocials?.discordUserName && <SocialUserNameBlock type="discord" userName={currentSocials?.discordUserName} />}
                                {currentSocials?.twitch && <SocialUserNameBlock type="twitch" userName={currentSocials.twitch.replace('https://www.twitch.tv/', '')} />}
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="flex gap-[12px] sm:gap-[6px] items-center">
                                <SocialLink className={`${socialLinkSize}`} type="steam" href={clearSteamLink(steamProfile?.profileurl)} />
                                <SocialLink className={`${socialLinkSize}`} type="discord" href={currentSocials?.discord} />
                                <SocialLink className={`${socialLinkSize}`} type="twitch" isLive={player.social?.isTwitchLive} href={currentSocials?.twitch} />
                                <SocialLink className={`${socialLinkSize}`} type="youtube" href={currentSocials?.youtube} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="">
                    {canChangeSocials && (
                        <>
                            <EditProfileButton onClick={() => setIsEditMediaOpen(true)} />
                            {isEditMediaOpen && (
                                <EditMediaPopup
                                    initialSocials={currentSocials ?? {}}
                                    onSave={handleSaveSocials}
                                    onClose={() => setIsEditMediaOpen(false)}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
        <div className="w-[370px] sm:w-[505px] flex flex-col bg-dark shadow-dark-l gap-[32px] p-[32px]">
            <h4 className="uppercase">
                {localize("player_statistic_title")}
            </h4>
            <div className="grid grid-cols-2 gap-x-[74px] gap-y-[32px] text-16">
                <div>
                    <div className={`${statiscTitleClass}`}>
                        {localize("player_rating")}
                    </div>
                    <div className={`${statiscValueClass}`}>{player.rating ?? 0}</div>
                </div>
                <div>
                    <div className={`${statiscTitleClass}`}>
                        {localize("player_total_games")}
                    </div>
                    <div className={`${statiscValueClass}`}>{player.matchCount}</div>
                </div>
                <div>
                    <div className={`${statiscTitleClass}`}>
                        {localize("player_first_places")}
                    </div>
                    <div className={`${statiscValueClass}`}>{player.firstPlaces}</div>
                </div>
                <div>
                    <div className={`${statiscTitleClass}`}>
                        {localize("player_avg_place")}
                    </div>
                    <div className={`${statiscValueClass}`}>{player.avgPlace?.toFixed(2) ?? 0}</div>
                </div>
                <div className="flex flex-col my-auto">
                    <div className={`${statiscTitleClass}`}>
                        {localize("player_places_title")}
                    </div>
                    <div className="flex ">
                        {<Places className="" placeClassName="sm:h-[40px]" places={player.places} />}
                    </div>
                </div>
                <div>
                    <div className={`${statiscTitleClass}`}>
                        {localize("player_favorite_hero_title")}
                    </div>
                    <div className={`${statiscValueClass}`}>
                        {player.favoriteHero && <HeroImage
                            className="shadow-dark-sm"
                            size="80x47"
                            heroName={player.favoriteHero} />}
                    </div>
                </div>
            </div>
        </div>
    </div>
}

const EmptyPlayerProfileBlock: React.FC = () => {
    return <div className="w-[505px]" >
        <div className="w-full h-[224px] bg-dark shadow-dark-l p-[32px]">
        </div>
        <div className="mt-[24px] w-full h-[260px]  bg-dark shadow-dark-l">
        </div>
    </div>
}

const SocialUserNameBlock: React.FC<{
    userName: string
    type: SocialType
}> = ({ userName, type }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!userName) return;

        try {
            await navigator.clipboard.writeText(userName);
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        } catch (err) {
            console.error("Copied error:", err);
        }
    };

    if (!userName) return null;

    return (
        <div className="relative group">
            <div
                onClick={handleCopy}
                className="flex flex-row gap-[2px] items-center text-[14px] text-gray font-['Roboto'] brightness-[80%] hover:brightness-[130%] cursor-pointer "
            >
                <SocialIcon
                    className="max-w-[15px] max-h-[15px]"
                    type={type}
                    isActive={true}
                />
                <span>{getShortString(userName, 15)}</span>
            </div>

            {copied && (
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-orange-dark text-[14px] font-['Roboto']">
                    {localize('copied')}
                </div>
            )}
        </div>
    );
};
